import { Component, ViewEncapsulation, viewChild } from '@angular/core';
import {
  ModalRef,
  ModalV2Component,
  RadioGroupOptionV2,
  ReactiveComponent,
  ModalModule,
  InputV2Module,
  RadioV2Module,
  SpinnerModule,
  ButtonV2Module,
} from '@schenck/rotec-components';
import { BehaviorSubject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { MachineService } from 'src/services/machine/machine.service';
import { Machine } from 'src/utils/interfaces/machine.interfaces';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'rm-add-machine-modal',
  templateUrl: './add-machine-modal.component.html',
  styleUrls: ['./add-machine-modal.component.less'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    ModalModule,
    InputV2Module,
    RadioV2Module,
    SpinnerModule,
    ButtonV2Module,
    TranslatePipe,
    AsyncPipe,
  ],
})
export class AddMachineModalComponent extends ReactiveComponent {
  /**
   * The width of the modal.
   */
  public width: string = '500px';

  /**
   * The default top height value for the modal.
   */
  public top: number = 70;

  /**
   * The title of the modal.
   */
  public title: string = 'Machines';

  /**
   * The right padding of the modal.
   */
  public right: string = '40px';

  /**
   * The options for the machine list
   */
  public machineOptions: RadioGroupOptionV2[];

  /**
   * The filtered machines
   */
  public filteredMachineOptions: RadioGroupOptionV2[];

  /**
   * The available machine list
   */
  public machines: Machine[];

  /**
   * The selected available machine
   */
  public selectedMachine: Machine;

  /**
   * For showing spinner when requests are loading
   */
  public isLoading$ = new BehaviorSubject<boolean>(true);

  readonly modalContainer = viewChild(ModalV2Component);

  constructor(
    public modalRef: ModalRef<number>,
    private readonly machineService: MachineService
  ) {
    super();
  }

  public ngOnInit() {
    super.ngOnInit();
    this.machineService.availableMachines$
      ?.pipe(
        takeUntil(this.onDestroy$),
        filter((machines: Machine[]) => !!machines)
      )
      .subscribe((machines: Machine[]) => {
        this.machines = machines;
        this.setMachineOptions();
        this.isLoading$.next(false);
      });
  }

  public ngAfterViewInit() {
    super.ngAfterViewInit();
    this.setModalPosition();
  }

  public selectMachine(id: string): void {
    this.selectedMachine = this.machines.find(({ assetId }) => assetId === id);
  }

  public close(): void {
    this.modalRef.close();
  }

  public apply(): void {
    if (this.selectedMachine) {
      this.modalRef.emit(this.selectedMachine);
    }

    this.modalRef.close();
  }

  public filterMachineOptions(searchInput: string): void {
    this.filteredMachineOptions = searchInput
      ? this.machineOptions.filter(({ name }) =>
          name.toLowerCase().match(searchInput.toLowerCase())
        )
      : null;
  }

  private setModalPosition(): void {
    const modalContainer = this.modalContainer().modal();
    const modalHeight = modalContainer.nativeElement.offsetHeight;
    const isOutOfWindow = window.innerHeight - modalHeight - this.modalRef.data <= 10;

    if (isOutOfWindow) {
      modalContainer.nativeElement.style.bottom = '10px';
    } else {
      modalContainer.nativeElement.style.top = `${+this.modalRef.data + this.top}px`;
    }
    modalContainer.nativeElement.style.position = 'absolute';
    modalContainer.nativeElement.style.right = this.right;
  }

  private setMachineOptions(): void {
    this.machineOptions = this.machines.map(({ assetId, name }: Machine) => ({
      name,
      value: assetId,
    }));
  }
}
